import { Building, Edit, Home, ParkingSquare, Plus } from 'lucide-react';
import {
  Accordion,
  Button,
  Card,
  Chip,
  Description,
  Tooltip,
} from '@heroui/react';
import type { Apartment, Block, ParkingSpot } from '../../../api/condominiums';
import {
  getParkingSpotStatusClasses,
  getParkingSpotStatusLabel,
} from '../../utils/parking-spot-status';
import { getTileFontSize, sortApartments } from '../../utils/sort-apartments';

interface BlockCardProps {
  block: Block;
  isSingleBlock: boolean;
  canEditDeleteBlocks: boolean;
  showInactiveBlocks: boolean;
  showInactiveSpots: boolean;
  onCreateApartment: (blockId: string) => void;
  onCreateSpot: (blockId: string) => void;
  onEditBlock: (block: Block) => void;
  onEditApartment: (apartment: Apartment, blockId: string) => void;
  onEditSpot: (spot: ParkingSpot, blockId: string) => void;
}

function ApartmentTile({
  apartment,
  onPress,
}: {
  apartment: Apartment;
  onPress: () => void;
}) {
  const hasMembers = (apartment.memberCount ?? 0) > 0;
  const fontSize = getTileFontSize(apartment.number);

  const tile = (
    <button
      type="button"
      onClick={onPress}
      className={`relative flex min-h-[100px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-separator bg-surface-secondary p-2 transition-opacity hover:opacity-80 ${!apartment.isActive ? 'opacity-60' : ''}`}
    >
      {!hasMembers ? (
        <span className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-warning text-[10px] font-bold text-warning-foreground shadow">
          !
        </span>
      ) : (
        <span className="absolute -right-2 -top-2 flex size-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground shadow">
          {apartment.memberCount}
        </span>
      )}
      <span className={`${fontSize} px-2 text-center font-bold leading-tight break-words`}>
        {apartment.number}
      </span>
      {apartment.floor ? (
        <p className="px-1 text-center text-xs text-muted break-words">Andar {apartment.floor}</p>
      ) : null}
    </button>
  );

  if (!hasMembers) {
    return (
      <Tooltip delay={500}>
        <Tooltip.Trigger>{tile}</Tooltip.Trigger>
        <Tooltip.Content>
          Nenhum morador associado. Clique para editar e associar.
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return tile;
}

function SpotTile({
  spot,
  apartments,
  onPress,
}: {
  spot: ParkingSpot;
  apartments: Apartment[];
  onPress: () => void;
}) {
  const styles = getParkingSpotStatusClasses(spot.status);
  const fontSize = getTileFontSize(spot.code);
  const linkedApartment = apartments.find((apt) => apt.id === spot.apartmentId);
  const details = [
    linkedApartment
      ? `Apartamento: ${linkedApartment.number}${linkedApartment.floor ? ` (${linkedApartment.floor})` : ''}`
      : spot.apartmentId
        ? 'Apartamento: vinculado'
        : 'Apartamento: não vinculado',
    spot.location && `Localização: ${spot.location}`,
    spot.level && `Nível: ${spot.level}`,
    spot.notes && `Obs: ${spot.notes}`,
    `Status: ${getParkingSpotStatusLabel(spot.status)}`,
    `Ativa: ${spot.isActive ? 'Sim' : 'Não'}`,
  ]
    .filter(Boolean)
    .join('\n');

  const tile = (
    <button
      type="button"
      onClick={onPress}
      className={`relative flex min-h-[100px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border p-2 transition-opacity hover:opacity-80 ${styles.border} ${styles.bg} ${!spot.isActive ? 'opacity-60' : ''}`}
    >
      <span className={`${fontSize} ${styles.text} px-1 text-center font-bold leading-tight break-words`}>
        {spot.code}
      </span>
      {spot.level ? (
        <p className="px-1 text-center text-xs text-muted break-words">{spot.level}</p>
      ) : null}
    </button>
  );

  return (
    <Tooltip delay={500}>
      <Tooltip.Trigger>{tile}</Tooltip.Trigger>
      <Tooltip.Content>{details || 'Sem detalhes adicionais'}</Tooltip.Content>
    </Tooltip>
  );
}

export function BlockCard({
  block,
  isSingleBlock,
  canEditDeleteBlocks,
  showInactiveBlocks,
  showInactiveSpots,
  onCreateApartment,
  onCreateSpot,
  onEditBlock,
  onEditApartment,
  onEditSpot,
}: BlockCardProps) {
  const filteredSpots = showInactiveSpots
    ? block.parkingSpots
    : block.parkingSpots.filter((s) => s.isActive);

  const apartments = sortApartments(
    (block.apartments ?? []).filter((a) => showInactiveBlocks || a.isActive),
  );

  return (
    <Card className="transition-shadow hover:shadow-md">
      <Card.Content className="flex flex-col gap-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Card.Title>{block.name}</Card.Title>
            {block.code && block.code !== 'UNICO' ? (
              <Card.Description>Código: {block.code}</Card.Description>
            ) : null}
            {isSingleBlock ? (
              <Description className="text-xs italic">Bloco único padrão</Description>
            ) : null}
            <Description className="mt-1">
              {block.apartments?.length ?? 0} apartamento(s) · {block.parkingSpots.length} vaga(s)
            </Description>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!block.isActive ? (
              <Chip size="sm" variant="soft">
                Inativo
              </Chip>
            ) : null}
            {block.isActive ? (
              <>
                <Button variant="secondary" size="sm" onPress={() => onCreateApartment(block.id)}>
                  <Home size={16} />
                  Apto
                </Button>
                <Button variant="primary" size="sm" onPress={() => onCreateSpot(block.id)}>
                  <Plus size={16} />
                  Vaga
                </Button>
              </>
            ) : null}
            {canEditDeleteBlocks ? (
              <Button
                isIconOnly
                variant="secondary"
                size="sm"
                aria-label="Editar bloco"
                onPress={() => onEditBlock(block)}
              >
                <Edit size={16} />
              </Button>
            ) : null}
          </div>
        </div>

        <Accordion defaultExpandedKeys={['apartments', 'parking-spots']}>
          <Accordion.Item id="apartments">
            <Accordion.Heading>
              <Accordion.Trigger>
                <span className="flex items-center gap-2">
                  <Home size={18} />
                  Apartamentos
                  <Chip size="sm" variant="soft">
                    {block.apartments?.length ?? 0}
                  </Chip>
                </span>
                <Accordion.Indicator />
              </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel>
              <Accordion.Body>
                {apartments.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3 pt-2 sm:grid-cols-6 md:grid-cols-8">
                    {apartments.map((apt) => (
                      <ApartmentTile
                        key={apt.id}
                        apartment={apt}
                        onPress={() => onEditApartment(apt, block.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-muted">
                    <Home className="mx-auto mb-2 opacity-40" size={32} />
                    <p>Nenhum apartamento neste bloco</p>
                    {block.isActive ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="mt-3"
                        onPress={() => onCreateApartment(block.id)}
                      >
                        Adicionar apartamento
                      </Button>
                    ) : null}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item id="parking-spots">
            <Accordion.Heading>
              <Accordion.Trigger>
                <span className="flex items-center gap-2">
                  <ParkingSquare size={18} />
                  Vagas
                  <Chip size="sm" variant="soft" color="accent">
                    {block.parkingSpots.length}
                  </Chip>
                </span>
                <Accordion.Indicator />
              </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel>
              <Accordion.Body>
                {filteredSpots.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3 pt-2 sm:grid-cols-6 md:grid-cols-8">
                    {filteredSpots.map((spot) => (
                      <SpotTile
                        key={spot.id}
                        spot={spot}
                        apartments={block.apartments ?? []}
                        onPress={() => onEditSpot(spot, block.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-muted">
                    <ParkingSquare className="mx-auto mb-2 opacity-40" size={32} />
                    {block.parkingSpots.length === 0 ? (
                      <>
                        <p>Nenhuma vaga neste bloco</p>
                        {block.isActive ? (
                          <Button
                            variant="primary"
                            size="sm"
                            className="mt-3"
                            onPress={() => onCreateSpot(block.id)}
                          >
                            Adicionar vaga
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <p>Habilite &quot;Mostrar vagas inativas&quot; para vê-las.</p>
                    )}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Card.Content>
    </Card>
  );
}

export function AddBlockCard({ onCreateBlock }: { onCreateBlock: () => void }) {
  return (
    <Card className="border border-dashed border-separator">
      <Card.Content className="flex min-h-[280px] flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Building size={32} />
        </div>
        <div>
          <Card.Title>Adicionar bloco</Card.Title>
          <Card.Description className="mt-2">
            Crie blocos ou torres para organizar apartamentos e vagas.
          </Card.Description>
        </div>
        <Button variant="primary" onPress={onCreateBlock}>
          <Plus size={18} />
          Novo bloco
        </Button>
      </Card.Content>
    </Card>
  );
}
